/**
 * ADMIN PIN VERIFICATION MODAL
 * 
 * Specialized modal for admin two-factor authentication using PIN codes
 * sent to jp.rademeyer84@gmail.com with professional styling and security features.
 * 
 * Features:
 * - Professional admin-focused design
 * - Real-time PIN validation
 * - Loading states and error handling
 * - Rate limiting feedback
 * - Security-focused messaging
 * - Consistent with existing dashboard theme
 */

import React, { useState, useEffect, useRef } from 'react'
import { AdminPinService } from '../lib/adminPinService'

interface AdminPinVerificationModalProps {
  isOpen: boolean
  onClose: () => void
  onVerified: (loginToken?: string) => void
  adminEmail: string
  password?: string
  title?: string
  description?: string
}

export const AdminPinVerificationModal: React.FC<AdminPinVerificationModalProps> = ({
  isOpen,
  onClose,
  onVerified,
  adminEmail,
  password,
  title = '🔐 Admin Authentication Required',
  description = 'Two-factor authentication is required for admin access. A PIN has been sent to the security email address.'
}) => {
  const [pin, setPin] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [sendingPin, setSendingPin] = useState(false)
  const [pinSent, setPinSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [rateLimited, setRateLimited] = useState(false)
  const [checkingLink, setCheckingLink] = useState(false)
  // v4.7.69: TOTP-first flow. mfaMode is determined by a single check_mfa probe
  // when the modal opens. 'unknown' = probing; 'email_pin' = legacy email PIN
  // path (admin has no authenticator enrolled); 'totp' = mandatory authenticator
  // path (email PIN is permanently disabled server-side for this admin).
  const [mfaMode, setMfaMode] = useState<'unknown' | 'email_pin' | 'totp'>('unknown')
  const [probing, setProbing] = useState(false)
  const [useBackupCode, setUseBackupCode] = useState(false)

  const pinInputRef = useRef<HTMLInputElement>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  // v4.7.69: ref guards the probe against React re-running the effect when
  // setProbing(true) flips state — without this, the effect's cleanup would
  // cancel the in-flight fetch and leave the modal hung on "Checking…".
  const probeStartedRef = useRef(false)

  // v4.7.69: On open, probe MFA state. If TOTP is enrolled we MUST NOT trigger
  // the email-PIN send path (the server will refuse it anyway, but skipping the
  // call avoids confusing rate-limit feedback and the misleading "PIN sent"
  // notice). For email-PIN admins, preserve the legacy auto-send behaviour.
  useEffect(() => {
    if (!isOpen) return
    if (mfaMode !== 'unknown') return
    if (probeStartedRef.current) return
    probeStartedRef.current = true
    setProbing(true)
    setError(null)
    AdminPinService.checkMfa(adminEmail, password)
      .then(result => {
        if (result.totpEnabled) {
          console.log('🔐 Admin TOTP enrolled — switching to authenticator flow')
          setMfaMode('totp')
        } else {
          console.log('🔐 Admin has no TOTP — using email PIN flow')
          setMfaMode('email_pin')
          if (!pinSent && !rateLimited) {
            sendPin()
          }
        }
      })
      .catch(err => {
        console.error('Failed to probe admin MFA state:', err)
        setError('Could not determine authentication method. Please retry.')
        // Allow a manual retry by clearing the ref on hard error
        probeStartedRef.current = false
      })
      .finally(() => {
        setProbing(false)
      })
  }, [isOpen, adminEmail, mfaMode])

  // Focus PIN/TOTP input when ready
  useEffect(() => {
    if (!isOpen || !pinInputRef.current) return
    // v4.7.69: focus once we're in TOTP mode, or once the legacy email PIN
    // has been sent.
    if (mfaMode === 'totp' || pinSent) {
      setTimeout(() => {
        pinInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, pinSent, mfaMode, useBackupCode])

  // Poll for email-link verification (legacy email-PIN flow only).
  // v4.7.69: never poll when the admin is on the TOTP path; the email link
  // pathway is disabled for those accounts.
  useEffect(() => {
    if (mfaMode !== 'email_pin') return
    if (pinSent && !success && isOpen) {
      console.log('🔗 Starting link verification polling...')
      setCheckingLink(true)

      pollIntervalRef.current = setInterval(async () => {
        try {
          const result = await AdminPinService.checkLinkVerification(adminEmail)
          if (result.verified && result.verifiedViaLink) {
            console.log('✅ Admin login verified via email link!')
            setSuccess(true)
            setCheckingLink(false)
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current)
            }
            setTimeout(() => {
              onVerified(result.loginToken)  // v7.3.316: pass loginToken
              handleClose()
            }, 1000)
          }
        } catch (err) {
          console.error('Error polling for link verification:', err)
        }
      }, 5000) // Check every 5 seconds (reduced from 2s to prevent rate limit issues)

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
        }
      }
    }
  }, [pinSent, success, isOpen, adminEmail, mfaMode])

  // Countdown timer for rate limiting
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else if (countdown === 0 && rateLimited) {
      setRateLimited(false)
    }
  }, [countdown, rateLimited])

  const sendPin = async () => {
    setSendingPin(true)
    setError(null)

    try {
      console.log('📧 Sending admin PIN to security email...')
      const result = await AdminPinService.generateAndSendPin(adminEmail, password)
      
      if (result.success) {
        setPinSent(true)
        console.log('✅ Admin PIN sent successfully')
      } else {
        console.error('❌ Failed to send admin PIN:', result.error)
        setError(result.error || 'Failed to send PIN')
        
        // Handle rate limiting
        if (result.error?.includes('wait')) {
          setRateLimited(true)
          const waitMinutes = parseInt(result.error.match(/\d+/)?.[0] || '5')
          setCountdown(waitMinutes * 60)
        }
      }
    } catch (error) {
      console.error('❌ Error sending admin PIN:', error)
      setError('Failed to send PIN')
    } finally {
      setSendingPin(false)
    }
  }

  const verifyPin = async () => {
    if (pin.length !== 6) {
      setError('Please enter a 6-digit PIN')
      return
    }

    setLoading(true)
    setError(null)

    try {
      console.log('🔍 Verifying admin PIN...')
      const result = await AdminPinService.verifyPin(adminEmail, pin)
      
      if (result.success) {
        setSuccess(true)
        console.log('✅ Admin PIN verified successfully')
        setTimeout(() => {
          onVerified(result.loginToken)  // v7.3.316: pass loginToken
          handleClose()
        }, 1000)
      } else {
        console.error('❌ Admin PIN verification failed:', result.error)
        setError(result.error || 'Invalid PIN')
        setPin('') // Clear PIN on error
      }
    } catch (error) {
      console.error('❌ Error verifying admin PIN:', error)
      setError('Failed to verify PIN')
      setPin('')
    } finally {
      setLoading(false)
    }
  }

  // v4.7.69: TOTP / backup-code verification for admins enrolled in MFA.
  // Backup codes are 8 digits, authenticator codes are 6 digits; the server
  // accepts either via the same verifyMFA() primitive.
  const verifyTotp = async () => {
    const expected = useBackupCode ? 8 : 6
    if (pin.length !== expected) {
      setError(useBackupCode
        ? 'Please enter your 8-digit backup code'
        : 'Please enter your 6-digit authenticator code')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const result = await AdminPinService.verifyTotp(adminEmail, pin, password)
      if (result.success) {
        setSuccess(true)
        setTimeout(() => {
          onVerified(result.loginToken)
          handleClose()
        }, 1000)
      } else {
        setError(result.error || 'Invalid authenticator code')
        setPin('')
      }
    } catch (err) {
      console.error('Error verifying admin TOTP:', err)
      setError('Failed to verify authenticator code')
      setPin('')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    // Clear polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    setPin('')
    setError(null)
    setSuccess(false)
    setPinSent(false)
    setCountdown(0)
    setRateLimited(false)
    setCheckingLink(false)
    // v4.7.69: also reset MFA probe state so the next open re-probes
    setMfaMode('unknown')
    setProbing(false)
    setUseBackupCode(false)
    probeStartedRef.current = false
    onClose()
  }

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // v4.7.69: accept up to 8 digits when entering a backup code
    const maxLen = mfaMode === 'totp' && useBackupCode ? 8 : 6
    const value = e.target.value.replace(/\D/g, '').slice(0, maxLen)
    setPin(value)
    setError(null)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || loading) return
    if (mfaMode === 'totp') {
      const expected = useBackupCode ? 8 : 6
      if (pin.length === expected) verifyTotp()
    } else if (pin.length === 6) {
      verifyPin()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg border border-gray-600 max-w-md w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          disabled={loading || sendingPin}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">
            {mfaMode === 'totp' ? '🔑 Authenticator Required' : title}
          </h2>
          <p className="text-gray-300 text-sm">
            {mfaMode === 'totp'
              ? 'Open your authenticator app and enter the current 6-digit code. Email PIN is disabled because an authenticator is enrolled on this account.'
              : description}
          </p>
        </div>

        {/* v4.7.69: MFA probe indicator */}
        {probing && mfaMode === 'unknown' && (
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
              <p className="text-blue-300 text-sm">
                🔍 Checking authentication method...
              </p>
            </div>
          </div>
        )}

        {/* PIN Status — email PIN flow only */}
        {mfaMode === 'email_pin' && sendingPin && (
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
              <p className="text-blue-300 text-sm">
                📧 Sending security PIN to admin email...
              </p>
            </div>
          </div>
        )}

        {mfaMode === 'email_pin' && pinSent && !success && (
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-4">
            <p className="text-green-300 text-sm text-center">
              ✅ Verification email sent to admin address
            </p>
            <p className="text-yellow-300 text-sm text-center mt-2">
              🔗 Click the link in your email OR enter the PIN below
            </p>
            {checkingLink && (
              <div className="flex items-center justify-center gap-2 mt-2">
                <div className="animate-pulse w-2 h-2 bg-yellow-400 rounded-full"></div>
                <p className="text-yellow-300 text-sm">
                  Waiting for email link verification...
                </p>
              </div>
            )}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-4">
            <p className="text-green-300 text-sm text-center">
              🎉 Admin authentication successful! Redirecting...
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p className="text-red-300 text-sm text-center">{error}</p>
          </div>
        )}

        {/* Rate Limiting Message */}
        {rateLimited && countdown > 0 && (
          <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4 mb-4">
            <p className="text-orange-300 text-sm text-center">
              ⏳ Rate limited. Next PIN available in {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
            </p>
          </div>
        )}

        {/* v4.7.69: TOTP / Backup Code Input */}
        {mfaMode === 'totp' && !success && (
          <div className="mb-6">
            <label htmlFor="admin-totp" className="block text-sm font-semibold text-gray-300 mb-3">
              {useBackupCode
                ? 'Enter 8-Digit Backup Code'
                : 'Enter 6-Digit Authenticator Code'}
            </label>
            <input
              ref={pinInputRef}
              id="admin-totp"
              type="text"
              value={pin}
              onChange={handlePinChange}
              onKeyPress={handleKeyPress}
              placeholder={useBackupCode ? '00000000' : '000000'}
              maxLength={useBackupCode ? 8 : 6}
              inputMode="numeric"
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-white text-center text-2xl font-mono tracking-widest"
              disabled={loading}
              autoComplete="one-time-code"
            />
            <button
              type="button"
              onClick={() => { setUseBackupCode(v => !v); setPin(''); setError(null) }}
              className="block mx-auto mt-3 text-xs text-blue-300 hover:text-blue-200 underline"
              disabled={loading}
            >
              {useBackupCode
                ? '← Use authenticator app code instead'
                : 'Lost your device? Use a backup code →'}
            </button>
          </div>
        )}

        {/* Email PIN Input (legacy path) */}
        {mfaMode === 'email_pin' && pinSent && !success && (
          <div className="mb-6">
            <label htmlFor="admin-pin" className="block text-sm font-semibold text-gray-300 mb-3">
              Enter 6-Digit Security PIN
            </label>
            <input
              ref={pinInputRef}
              id="admin-pin"
              type="text"
              value={pin}
              onChange={handlePinChange}
              onKeyPress={handleKeyPress}
              placeholder="000000"
              maxLength={6}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-white text-center text-2xl font-mono tracking-widest"
              disabled={loading}
              autoComplete="off"
            />
            <p className="text-white text-sm mt-2 text-center">
              PIN expires in 15 minutes • Maximum 3 attempts
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {mfaMode === 'totp' ? (
            <>
              <button
                onClick={handleClose}
                className="flex-1 py-3 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={verifyTotp}
                disabled={loading || pin.length !== (useBackupCode ? 8 : 6)}
                className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-800 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Verifying...
                  </>
                ) : (
                  '🔑 Verify & Login'
                )}
              </button>
            </>
          ) : !pinSent ? (
            <button
              onClick={sendPin}
              disabled={sendingPin || rateLimited || probing || mfaMode === 'unknown'}
              className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-800 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              {sendingPin ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending PIN...
                </>
              ) : rateLimited ? (
                `Wait ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}`
              ) : (
                '📧 Send Security PIN'
              )}
            </button>
          ) : (
            <>
              <button
                onClick={handleClose}
                className="flex-1 py-3 px-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={verifyPin}
                disabled={loading || pin.length !== 6}
                className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-800 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Verifying...
                  </>
                ) : (
                  '🔐 Verify & Login'
                )}
              </button>
            </>
          )}
        </div>

        {/* Security Notice */}
        <div className="mt-4 p-3 bg-gray-900/50 rounded-lg border border-gray-700">
          <p className="text-white text-sm text-center">
            🔒 This is a secure admin authentication process. All attempts are logged and monitored.
          </p>
        </div>
      </div>
    </div>
  )
}
